/**
 * Shared map types and interfaces for Leaflet map implementation
 */

import type { GPSCoordinates } from '../../jobs/types/inspectionNode'

/**
 * Map provider types
 */
export type MapProvider = 'leaflet'

/**
 * Locate button stage (005): off → following → heading-up → off.
 * Ephemeral hook state only — never persisted (FR-021).
 */
export type LocateMode = 'off' | 'following' | 'heading'

/**
 * Device-orientation compass availability (005).
 * 'denied' is sticky for the page session (matches iOS permission caching).
 */
export type CompassAvailability =
  | 'unknown'
  | 'needs-permission'
  | 'available'
  | 'denied'
  | 'unsupported'

/**
 * Snapshot of the layers currently shown on the interactive map, used to
 * assemble the ⓘ attribution credits (005). `imagerySourceId` is the
 * region-resolved satellite source (null when the street base is active);
 * `cadastreRegion` is the state/region key when property lines are on.
 */
export interface ActiveLayersState {
  baseLayer: 'satellite' | 'street'
  imagerySourceId: string | null
  labelsOn: boolean
  cadastreRegion: string | null
}

/**
 * Map initialization options
 */
export interface MapOptions {
  center?: [number, number] // [longitude, latitude]
  zoom?: number
}

/**
 * Marker configuration
 */
export interface MarkerConfig {
  coordinates: GPSCoordinates
  id: string
  color?: string
  scale?: number
  popup?: string | HTMLElement
  onClick?: () => void
}

/**
 * Map instance interface - provider-agnostic
 */
export interface IMapInstance {
  /** Whether the map has finished loading */
  isLoaded: boolean

  /** Any error that occurred during map initialization */
  error: string | null

  /** Fly to a specific location with optional zoom */
  flyTo: (center: [number, number], zoom?: number) => void

  /** Add a marker to the map */
  addMarker: (
    coordinates: GPSCoordinates,
    id: string,
    options?: {
      color?: string
      scale?: number
    },
    popup?: string | HTMLElement,
    onClick?: () => void
  ) => void

  /** Remove a marker by ID */
  removeMarker: (id: string) => void

  /** Clear all markers from the map */
  clearMarkers: () => void

  /** Fit bounds to show all coordinates */
  fitBounds: (coordinates: [number, number][]) => void

  /** Get the underlying map instance (provider-specific) */
  getMapInstance: () => unknown
}

/**
 * Default map configuration
 */
export const DEFAULT_MAP_CONFIG = {
  zoom: 18,
  pitch: 0,
  bearing: 0,
  center: [0, 0] as [number, number],
} as const

/**
 * Map provider configuration
 */
export interface MapProviderConfig {
  provider: MapProvider
  tileUrl?: string
  maxZoom?: number
  attribution?: string
}

/**
 * Tile layer sources for Leaflet
 */
export const TILE_SOURCES = {
  // Satellite imagery is region-resolved via features/map/services/imagerySources.ts
  // (feature 004) — no satellite entry here. The topographic layer was removed
  // in 004 (no authenticated raster equivalent of the retiring Esri endpoint).

  // OpenStreetMap (free, streets only)
  osm_streets: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',

  // CARTO labels only (transparent overlay)
  carto_labels_only: 'https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png',
} as const

/**
 * Cadastre tile sources for overlays (property boundaries)
 *
 * Using ArcGIS REST API tile services instead of WMS for better browser compatibility
 * These are direct tile URLs that work around CORS issues with WMS services
 */
export const CADASTRE_SOURCES = {
  // New South Wales Digital Cadastral Database (free, no API key required)
  nsw_cadastre: {
    url: 'https://maps.six.nsw.gov.au/arcgis/rest/services/public/NSW_Cadastre/MapServer/export',
    type: 'arcgis' as const,
    layers: 'show:9', // Lot layer (cadastre polygons)
    name: 'NSW',
    format: 'png32',
  },

  // Queensland Land Parcel Property Framework (free, no API key required)
  // Using ArcGIS REST API tiles instead of WMS
  qld_cadastre: {
    url: 'https://gisservices.information.qld.gov.au/arcgis/rest/services/PlanningCadastre/LandParcelPropertyFramework/MapServer/export',
    type: 'arcgis' as const,
    layers: 'show:3', // Lot layer
    name: 'QLD',
    format: 'png32',
  },

  // Tasmania LIST Cadastre and Administrative (free, Creative Commons license)
  // VERIFIED WORKING - Layer 38 is Cadastral Parcels
  tas_cadastre: {
    url: 'https://services.thelist.tas.gov.au/arcgis/rest/services/Public/CadastreAndAdministrative/MapServer/export',
    type: 'arcgis' as const,
    layers: 'show:38', // Cadastral Parcels layer
    name: 'TAS',
    format: 'png32',
  },

  // Victoria, SA, WA, ACT - Currently not working
  // VIC uses FeatureServer not MapServer, needs different implementation
  // SA, WA, ACT services timeout or require authentication
  // TODO: Research alternative public MapServer endpoints for these states

  // Northern Territory - Not yet available via public ArcGIS REST API
  // NTLIS requires access request for digital cadastral data
} as const

/**
 * Legacy WMS sources - kept for reference but not currently used
 * WMS services have CORS issues in browsers
 */
export const WMS_SOURCES_LEGACY = {
  // NSW Digital Cadastral Database (free, no API key required)
  nsw_cadastre: {
    url: 'http://maps.six.nsw.gov.au/arcgis/services/public/NSW_Cadastre/MapServer/WMSServer',
    layers: '0',
    name: 'NSW',
  },

  // Queensland Land Parcel Property Framework (free, no API key required)
  qld_cadastre: {
    url: 'https://spatial-gis.information.qld.gov.au/arcgis/services/PlanningCadastre/LandParcelPropertyFramework/MapServer/WMSServer',
    layers: '0', // Base land parcels layer
    name: 'QLD',
  },

  // Victoria Vicmap Property (free, no API key required)
  // Note: Vicmap services may be transitioning URLs in 2025
  vic_cadastre: {
    url: 'http://services.land.vic.gov.au/catalogue/publicproxy/guest/dv_geoserver/wms',
    layers: 'VMPROP_PROPERTY_MP', // Vicmap Property layer
    name: 'VIC',
  },

  // Western Australia Cadastre (free, but may require SLIP registration)
  // Note: Public WMS endpoint - simplified cadastre without full attributes
  wa_cadastre: {
    url: 'https://services.slip.wa.gov.au/public/services/SLIP_Public_Services/Transport_and_Infrastructure/MapServer/WMSServer',
    layers: '14', // Cadastre layer
    name: 'WA',
  },

  // New Zealand LINZ Parcels (free, requires API key from data.linz.govt.nz)
  // To use: Register at data.linz.govt.nz and replace YOUR_LINZ_API_KEY
  nz_parcels: {
    url: 'https://data.linz.govt.nz/services;key={linzApiKey}/wms',
    layers: 'layer-51571', // NZ Parcels layer
    name: 'NZ',
    requiresApiKey: true,
  },
} as const

/**
 * Region boundaries for automatic cadastre detection
 * Approximate lat/lng boundaries for each region
 */
export type RegionKey = keyof typeof REGION_BOUNDARIES

/**
 * Single point-in-region test shared by cadastre detection (useLeaflet's
 * detectRegion) and imagery resolution (imagerySources coverage predicates),
 * so the two can never disagree about which region a coordinate is in.
 * Boxes are approximate and may overlap — callers define precedence.
 */
export function isInRegion(lat: number, lng: number, region: RegionKey): boolean {
  const b = REGION_BOUNDARIES[region]
  return lat >= b.minLat && lat <= b.maxLat && lng >= b.minLng && lng <= b.maxLng
}

export const REGION_BOUNDARIES = {
  NSW: { minLat: -37.5, maxLat: -28, minLng: 141, maxLng: 154 },
  QLD: { minLat: -29, maxLat: -10, minLng: 138, maxLng: 154 },
  VIC: { minLat: -39, maxLat: -34, minLng: 140.9, maxLng: 150 },
  WA: { minLat: -35.2, maxLat: -13.5, minLng: 113, maxLng: 129 },
  SA: { minLat: -38, maxLat: -26, minLng: 129, maxLng: 141 },
  TAS: { minLat: -43.7, maxLat: -39.5, minLng: 143.8, maxLng: 148.5 },
  NT: { minLat: -26, maxLat: -11, minLng: 129, maxLng: 138 },
  ACT: { minLat: -35.9, maxLat: -35.1, minLng: 148.7, maxLng: 149.4 },
  NZ: { minLat: -47.3, maxLat: -34.4, minLng: 166.4, maxLng: 178.6 },
} as const

/**
 * Attribution strings for different tile sources
 */
export const TILE_ATTRIBUTIONS = {
  // Esri/satellite attribution lives on the imagery registry entries
  // (features/map/services/imagerySources.ts) since 004
  osm: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  carto:
    '© <a href="https://carto.com/attributions">CARTO</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  nsw: '© <a href="https://www.spatial.nsw.gov.au/">NSW Spatial Services</a>',
  qld: '© <a href="https://www.resources.qld.gov.au/">QLD Resources</a>',
  vic: '© <a href="https://www.land.vic.gov.au/">VIC Land</a>',
  sa: '© <a href="https://location.sa.gov.au/">SA Location Services</a>',
  wa: '© <a href="https://www.landgate.wa.gov.au/">Landgate WA</a>',
  tas: '© <a href="https://www.thelist.tas.gov.au/">LIST Tasmania</a>',
  act: '© <a href="https://actmapi.act.gov.au/">ACT Government</a>',
  nt: '© <a href="https://depws.nt.gov.au/">NT Government</a>',
  nz: '© <a href="https://www.linz.govt.nz/">LINZ</a>',
} as const
